import { useState, useRef } from 'react';
import * as XLSX from 'xlsx';
import type { CreateCompanyPayload } from '@/api/companies';
import {
  parseRowsByHeader,
  readExcelRows,
  readCSVRows,
  rowsToPayloads,
  type ParsedRow,
} from '@/lib/companyImport';

interface BatchImportModalProps {
  isOpen: boolean;
  onClose: () => void;
  onImport: (companies: CreateCompanyPayload[]) => void;
}

type PreviewRow = ParsedRow;

const CSV_TEMPLATE = '企业名称,统一信用代码,地址,网址,类型,负责人,业务标签(多个用;分隔),来源标签(多个用;分隔),联系人姓名,联系人电话,联系人职位\n示例公司,91110000XXXXXXXX,北京市朝阳区XX路XX号,www.example.com,实体企业,李经理,绿证;碳交易,绿色工厂,张三,13800000001,总经理';

function isSupportedFile(name: string): boolean {
  const ext = name.split('.').pop()?.toLowerCase();
  return ext === 'csv' || ext === 'xlsx' || ext === 'xls';
}

function isCSVFile(name: string): boolean {
  return name.split('.').pop()?.toLowerCase() === 'csv';
}

export default function BatchImportModal({ isOpen, onClose, onImport }: BatchImportModalProps) {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [preview, setPreview] = useState<PreviewRow[]>([]);
  const [fileName, setFileName] = useState('');
  const [importing, setImporting] = useState(false);
  const [error, setError] = useState('');
  const [showTemplateMenu, setShowTemplateMenu] = useState(false);

  if (!isOpen) return null;

  // rows 含表头行,按表头名称智能匹配列(顺序任意皆可对上)。
  const processDataRows = (rows: string[][]) => {
    const result = parseRowsByHeader(rows);
    if (result.fatalError) {
      setError(result.fatalError);
      return;
    }
    if (result.rows.length === 0) {
      setError('未能解析到有效数据行');
      return;
    }
    setPreview(result.rows);
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    setError('');
    if (!file) return;

    if (!isSupportedFile(file.name)) {
      setError('仅支持 .csv、.xlsx、.xls 格式文件');
      return;
    }

    setFileName(file.name);

    if (isCSVFile(file.name)) {
      const reader = new FileReader();
      reader.onload = (evt) => {
        try {
          const text = evt.target?.result as string;
          processDataRows(readCSVRows(text));
        } catch {
          setError('CSV 文件解析失败，请检查文件编码是否为 UTF-8');
        }
      };
      reader.onerror = () => setError('文件读取失败');
      reader.readAsText(file, 'UTF-8');
    } else {
      const reader = new FileReader();
      reader.onload = (evt) => {
        try {
          const buffer = evt.target?.result as ArrayBuffer;
          processDataRows(readExcelRows(buffer));
        } catch {
          setError('Excel 文件解析失败，请检查文件格式');
        }
      };
      reader.onerror = () => setError('文件读取失败');
      reader.readAsArrayBuffer(file);
    }
  };

  const handleImport = () => {
    const validRows = preview.filter((r) => r.valid);
    if (validRows.length === 0) {
      setError('没有有效数据可导入');
      return;
    }

    setImporting(true);
    onImport(rowsToPayloads(validRows));
    setImporting(false);
    handleReset();
    onClose();
  };

  const handleReset = () => {
    setPreview([]);
    setFileName('');
    setError('');
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleClose = () => {
    handleReset();
    onClose();
  };

  const downloadCSVTemplate = () => {
    const blob = new Blob(['\uFEFF' + CSV_TEMPLATE], { type: 'text/csv;charset=UTF-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = '企业导入模板.csv';
    a.click();
    URL.revokeObjectURL(url);
  };

  const downloadExcelTemplate = () => {
    const headerRow = ['企业名称', '统一信用代码', '地址', '网址', '类型', '负责人', '业务标签(多个用;分隔)', '来源标签(多个用;分隔)', '联系人姓名', '联系人电话', '联系人职位'];
    const sampleRow = ['示例公司', '91110000XXXXXXXX', '北京市朝阳区XX路XX号', 'www.example.com', '实体企业', '李经理', '绿证;碳交易', '绿色工厂', '张三', '13800000001', '总经理'];
    const ws = XLSX.utils.aoa_to_sheet([headerRow, sampleRow]);
    ws['!cols'] = headerRow.map(() => ({ wch: 22 }));
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, '企业数据');
    XLSX.writeFile(wb, '企业导入模板.xlsx');
  };

  return (
    <div
      className="fixed inset-0 z-[70] flex items-center justify-center p-4"
      onClick={handleClose}
    >
      <div className="absolute inset-0 bg-foreground-950/40" />
      <div
        className="relative bg-background-50 rounded-2xl w-full max-w-[900px] max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="sticky top-0 bg-background-50 border-b border-background-200/70 px-6 py-4 flex items-center justify-between z-10">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-accent-100 flex items-center justify-center flex-shrink-0">
              <i className="ri-file-upload-line text-lg text-accent-600"></i>
            </div>
            <div>
              <h2 className="text-lg font-heading font-bold text-foreground-900">批量导入企业</h2>
              <p className="text-xs text-foreground-500">上传 CSV 或 Excel 文件，快速导入企业数据</p>
            </div>
          </div>
          <button
            onClick={handleClose}
            className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-background-100 text-foreground-500 transition-colors cursor-pointer"
          >
            <i className="ri-close-line text-lg"></i>
          </button>
        </div>

        {/* Body */}
        <div className="p-6 space-y-5">
          {/* Upload area */}
          {preview.length === 0 ? (
            <>
              <div className="border-2 border-dashed border-background-300 rounded-xl p-8 text-center">
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".csv,.xlsx,.xls"
                  onChange={handleFileChange}
                  className="hidden"
                />
                <div className="flex flex-col items-center gap-3">
                  <div className="w-14 h-14 rounded-full bg-accent-50 flex items-center justify-center">
                    <i className="ri-file-excel-2-line text-2xl text-accent-500"></i>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-foreground-800">拖拽或点击上传文件</p>
                    <p className="text-xs text-foreground-500 mt-1">支持 .csv、.xlsx、.xls 格式，UTF-8 编码</p>
                  </div>
                  <button
                    onClick={() => fileInputRef.current?.click()}
                    className="px-4 py-2 bg-primary-500 hover:bg-primary-600 text-background-50 rounded-lg text-sm font-medium transition-colors cursor-pointer whitespace-nowrap"
                  >
                    <i className="ri-folder-open-line mr-1.5"></i>
                    选择文件
                  </button>
                </div>
              </div>

              {error && (
                <div className="bg-red-50 border border-red-200 rounded-lg px-4 py-3 flex items-start gap-2">
                  <i className="ri-error-warning-line text-red-500 mt-0.5"></i>
                  <p className="text-sm text-red-700">{error}</p>
                </div>
              )}

              {/* Template download */}
              <div className="bg-background-100 rounded-xl p-5">
                <h3 className="text-sm font-heading font-semibold text-foreground-800 mb-3">文件格式说明（CSV / Excel 通用）</h3>
                <div className="overflow-x-auto">
                  <table className="w-full text-xs">
                    <thead>
                      <tr className="border-b border-background-200">
                        <th className="text-left py-2 px-2 font-medium text-foreground-600 whitespace-nowrap">列名</th>
                        <th className="text-left py-2 px-2 font-medium text-foreground-600 whitespace-nowrap">说明</th>
                        <th className="text-left py-2 px-2 font-medium text-foreground-600 whitespace-nowrap">必填</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-background-200/70">
                      <tr>
                        <td className="py-2 px-2 text-foreground-800 font-medium">企业名称</td>
                        <td className="py-2 px-2 text-foreground-600">公司全称</td>
                        <td className="py-2 px-2"><span className="text-red-500">是</span></td>
                      </tr>
                      <tr>
                        <td className="py-2 px-2 text-foreground-800 font-medium">统一信用代码</td>
                        <td className="py-2 px-2 text-foreground-600">18位统一社会信用代码</td>
                        <td className="py-2 px-2"><span className="text-red-500">是</span></td>
                      </tr>
                      <tr>
                        <td className="py-2 px-2 text-foreground-800 font-medium">地址</td>
                        <td className="py-2 px-2 text-foreground-600">公司注册/办公地址</td>
                        <td className="py-2 px-2"><span className="text-red-500">是</span></td>
                      </tr>
                      <tr>
                        <td className="py-2 px-2 text-foreground-800 font-medium">网址</td>
                        <td className="py-2 px-2 text-foreground-600">公司官网（可选）</td>
                        <td className="py-2 px-2"><span className="text-foreground-400">否</span></td>
                      </tr>
                      <tr>
                        <td className="py-2 px-2 text-foreground-800 font-medium">类型</td>
                        <td className="py-2 px-2 text-foreground-600">实体企业 / 认证机构 / 第三方咨询机构</td>
                        <td className="py-2 px-2"><span className="text-foreground-400">否</span></td>
                      </tr>
                      <tr>
                        <td className="py-2 px-2 text-foreground-800 font-medium">负责人</td>
                        <td className="py-2 px-2 text-foreground-600">企业负责人姓名（可选）</td>
                        <td className="py-2 px-2"><span className="text-foreground-400">否</span></td>
                      </tr>
                      <tr>
                        <td className="py-2 px-2 text-foreground-800 font-medium">业务标签</td>
                        <td className="py-2 px-2 text-foreground-600">多个用 ; 分隔，如：绿证;碳交易</td>
                        <td className="py-2 px-2"><span className="text-foreground-400">否</span></td>
                      </tr>
                      <tr>
                        <td className="py-2 px-2 text-foreground-800 font-medium">来源标签</td>
                        <td className="py-2 px-2 text-foreground-600">多个用 ; 分隔，如：绿色工厂;碳中和先锋</td>
                        <td className="py-2 px-2"><span className="text-foreground-400">否</span></td>
                      </tr>
                      <tr>
                        <td className="py-2 px-2 text-foreground-800 font-medium">联系人姓名</td>
                        <td className="py-2 px-2 text-foreground-600">主要联系人（可选）</td>
                        <td className="py-2 px-2"><span className="text-foreground-400">否</span></td>
                      </tr>
                      <tr>
                        <td className="py-2 px-2 text-foreground-800 font-medium">联系人电话</td>
                        <td className="py-2 px-2 text-foreground-600">联系电话（可选）</td>
                        <td className="py-2 px-2"><span className="text-foreground-400">否</span></td>
                      </tr>
                      <tr>
                        <td className="py-2 px-2 text-foreground-800 font-medium">联系人职位</td>
                        <td className="py-2 px-2 text-foreground-600">职位名称（可选）</td>
                        <td className="py-2 px-2"><span className="text-foreground-400">否</span></td>
                      </tr>
                    </tbody>
                  </table>
                </div>
                <div className="relative inline-block">
                  <button
                    onClick={() => setShowTemplateMenu(!showTemplateMenu)}
                    className="mt-4 inline-flex items-center gap-1.5 px-3 py-2 bg-background-50 border border-background-200 hover:border-accent-300 rounded-lg text-xs font-medium text-accent-600 transition-colors cursor-pointer whitespace-nowrap"
                  >
                    <i className="ri-download-line text-sm"></i>
                    下载模板
                    <i className="ri-arrow-down-s-line text-sm"></i>
                  </button>
                  {showTemplateMenu && (
                    <>
                      <div className="fixed inset-0 z-10" onClick={() => setShowTemplateMenu(false)} />
                      <div className="absolute top-full mt-1 left-0 z-20 bg-background-50 rounded-lg border border-background-200 shadow-sm py-1 min-w-[170px]">
                        <button
                          onClick={() => { downloadCSVTemplate(); setShowTemplateMenu(false); }}
                          className="w-full flex items-center gap-2 px-4 py-2.5 text-xs text-foreground-700 hover:bg-background-100 transition-colors cursor-pointer whitespace-nowrap"
                        >
                          <i className="ri-file-text-line text-accent-500"></i>
                          CSV 模板 (.csv)
                        </button>
                        <button
                          onClick={() => { downloadExcelTemplate(); setShowTemplateMenu(false); }}
                          className="w-full flex items-center gap-2 px-4 py-2.5 text-xs text-foreground-700 hover:bg-background-100 transition-colors cursor-pointer whitespace-nowrap"
                        >
                          <i className="ri-file-excel-2-line text-green-500"></i>
                          Excel 模板 (.xlsx)
                        </button>
                      </div>
                    </>
                  )}
                </div>
              </div>
            </>
          ) : (
            <>
              {/* Preview */}
              <div>
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-2">
                    <i className="ri-file-list-3-line text-foreground-500"></i>
                    <span className="text-sm font-medium text-foreground-800">
                      预览：{fileName}
                    </span>
                    <span className="text-xs text-foreground-500">
                      （{preview.filter((r) => r.valid).length}/{preview.length} 条有效）
                    </span>
                  </div>
                  <button
                    onClick={handleReset}
                    className="text-xs text-foreground-500 hover:text-foreground-700 transition-colors cursor-pointer whitespace-nowrap"
                  >
                    <i className="ri-refresh-line mr-1"></i>
                    重新选择
                  </button>
                </div>

                <div className="overflow-x-auto border border-background-200 rounded-xl">
                  <table className="w-full text-xs">
                    <thead>
                      <tr className="bg-background-100 border-b border-background-200">
                        <th className="text-left px-3 py-2 font-medium text-foreground-600 whitespace-nowrap">#</th>
                        <th className="text-left px-3 py-2 font-medium text-foreground-600 whitespace-nowrap">企业名称</th>
                        <th className="text-left px-3 py-2 font-medium text-foreground-600 whitespace-nowrap">类型</th>
                        <th className="text-left px-3 py-2 font-medium text-foreground-600 whitespace-nowrap">业务标签</th>
                        <th className="text-left px-3 py-2 font-medium text-foreground-600 whitespace-nowrap">联系人</th>
                        <th className="text-left px-3 py-2 font-medium text-foreground-600 whitespace-nowrap">状态</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-background-100">
                      {preview.map((row) => (
                        <tr key={row.index} className={row.valid ? '' : 'bg-red-50/50'}>
                          <td className="px-3 py-2 text-foreground-500">{row.index}</td>
                          <td className="px-3 py-2 text-foreground-800 font-medium max-w-[200px] truncate">{row.name}</td>
                          <td className="px-3 py-2">
                            <span className="inline-flex px-2 py-0.5 bg-background-100 rounded text-foreground-600">
                              {row.type || '实体企业'}
                            </span>
                          </td>
                          <td className="px-3 py-2 text-foreground-600 max-w-[150px] truncate">{row.businessTags || '-'}</td>
                          <td className="px-3 py-2 text-foreground-600">{row.contactName || '-'}</td>
                          <td className="px-3 py-2">
                            {row.valid ? (
                              <span className="inline-flex items-center gap-1 text-accent-600">
                                <i className="ri-check-line text-sm"></i> 有效
                              </span>
                            ) : (
                              <span className="inline-flex items-center gap-1 text-red-500" title={row.error}>
                                <i className="ri-close-line text-sm"></i> {row.error}
                              </span>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              {error && (
                <div className="bg-red-50 border border-red-200 rounded-lg px-4 py-3 flex items-start gap-2">
                  <i className="ri-error-warning-line text-red-500 mt-0.5"></i>
                  <p className="text-sm text-red-700">{error}</p>
                </div>
              )}
            </>
          )}
        </div>

        {/* Footer */}
        {preview.length > 0 && (
          <div className="sticky bottom-0 bg-background-50 border-t border-background-200/70 px-6 py-4 flex items-center justify-between">
            <p className="text-xs text-foreground-500">
              将导入 <strong className="text-foreground-700">{preview.filter((r) => r.valid).length}</strong> 条有效数据
            </p>
            <div className="flex items-center gap-3">
              <button
                onClick={handleClose}
                className="px-4 py-2.5 rounded-lg text-sm font-medium text-foreground-600 hover:text-foreground-800 hover:bg-background-100 transition-colors cursor-pointer whitespace-nowrap"
              >
                取消
              </button>
              <button
                onClick={handleImport}
                disabled={importing || preview.filter((r) => r.valid).length === 0}
                className="flex items-center gap-2 px-5 py-2.5 bg-accent-500 hover:bg-accent-600 disabled:bg-accent-400 text-background-50 rounded-lg text-sm font-medium transition-colors cursor-pointer whitespace-nowrap"
              >
                {importing ? (
                  <>
                    <i className="ri-loader-4-line animate-spin text-base"></i>
                    导入中...
                  </>
                ) : (
                  <>
                    <i className="ri-upload-line text-base"></i>
                    确认导入
                  </>
                )}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}